/* The catalogue filters that travel between screens (2026-09-11 feedback,
   widened 2026-09-13).

   Products, «Доступ позиций», every Prices tab and Склад list the same cards,
   and a person working one supplier's assortment narrows each of them the same
   way. Typing the filters again on every page was the complaint — so they are
   remembered once, per user and company, and each of those screens starts from
   them and writes back to them.

   2026-09-13: «практически одна база данных — немножко унифицируем». The
   category and subcategory travel too, and every one of those screens draws
   the same first filter row ($components/CatalogFilterBar). Client / country
   pickers and the column layouts stay per screen: the columns differ from
   table to table, and a client filter means nothing on the Products page. */
import type { AuthState } from "./auth";
import type { Category } from "./catalog";
import { can } from "./permissions";
import { prefKey, readPref, writePref } from "./prefs";
import { hasSurface } from "./surfaces";

export type CatalogFilters = {
  q: string;
  supplier: string;
  category: number | null;
  subcategory: number | null;
};

const NAME = "catalog.filters.v1";

const idOrNull = (v: unknown): number | null =>
  typeof v === "number" && Number.isInteger(v) ? v : null;

export function loadCatalogFilters(userId: number | undefined, slug: string): CatalogFilters {
  const saved = readPref<Partial<CatalogFilters>>(prefKey(NAME, userId, slug));
  return {
    q: typeof saved?.q === "string" ? saved.q : "",
    supplier: typeof saved?.supplier === "string" ? saved.supplier : "",
    category: idOrNull(saved?.category),
    subcategory: idOrNull(saved?.subcategory),
  };
}

/** Writes the given filters over the remembered ones. A screen passes only
 *  what it shares — Склад keeps its search to itself, because there it also
 *  matches batch numbers. */
export function saveCatalogFilters(
  userId: number | undefined,
  slug: string,
  filters: Partial<CatalogFilters>,
): void {
  writePref(prefKey(NAME, userId, slug), { ...loadCatalogFilters(userId, slug), ...filters });
}

/** The remembered pickers, minus anything the category tree no longer has —
 *  a deleted category left selected would list nothing. */
export function knownCategories(
  categories: Category[],
  category: number | null,
  subcategory: number | null,
): { category: number | null; subcategory: number | null } {
  const byId = new Map(categories.map((c) => [c.id, c]));
  const top = category != null && byId.get(category)?.parent_id == null && byId.has(category) ? category : null;
  const sub = top != null && subcategory != null && byId.get(subcategory)?.parent_id === top ? subcategory : null;
  return { category: top, subcategory: sub };
}

/** Whether a card filed under `catId` passes the pickers. A card stores its
 *  leaf, so a category takes its subcategories with it; a subcategory is only
 *  itself. */
export function inCategory(
  categories: Category[],
  catId: number | null,
  category: number | null,
  subcategory: number | null,
): boolean {
  // A remembered id the tree no longer has — or a tree that failed to load —
  // narrows nothing rather than emptying the screen.
  const has = (id: number) => categories.some((c) => c.id === id);
  if (subcategory != null && has(subcategory)) return catId === subcategory;
  if (category == null || !has(category)) return true;
  if (catId === category) return true;
  return catId != null && categories.some((c) => c.id === catId && c.parent_id === category);
}

/** Where a supplier's «Каталог товаров» opens from a partner row (owner,
 *  2026-09-22): our Products page narrowed to the cards tagged with that
 *  supplier, with every way of adding goods there. A company without the
 *  products section still browses the supplier's own catalogue. */
export function supplierCatalogHref(
  auth: AuthState,
  slug: string,
  supplierName: string,
  sellerSlug: string,
): string {
  if (supplierName && hasSurface(auth, slug, "nav.products") && can(auth, slug, "products", "view")) {
    return `#/t/${slug}/products?supplier=${encodeURIComponent(supplierName)}`;
  }
  return `#/t/${slug}/suppliers/${sellerSlug}/products`;
}
